import { useMemo } from 'react'
import { ClientApplication } from '@numeo/types-core'
import { useAuth } from 'hooks/auth'

/** Pre-`settings.company` applications kept the company name at the application root. */
interface LegacyCompanyField {
    company?: { name?: string }
}

export interface ActiveCompanyIdentity {
    /** Display name, resolved through every source we have and falling back to the project name. */
    companyName: string
    /**
     * False when `companyName` is only the project name (`ali_star_ext_23601`). Callers that render
     * the name as a headline fall back to a quieter layout instead of shouting a slug.
     */
    hasRealCompanyName: boolean
    mcNumber?: string
    dotNumber?: string
    projectName?: string
    applicationId?: string
    /** Informational only: the org may hold several companies and more than one exists today. */
    isMultiCompany: boolean
    companyCount: number
}

/**
 * Who the dispatcher is acting as right now — one company, i.e. one MC, one Application.
 *
 * The resolution order was copied into four pages before this hook existed, each with a slightly
 * different middle fallback. This is the superset of all of them: live settings first (so a rename
 * in Settings → General shows up without a refresh), then the organization summary (hydrated at
 * login, refreshed on /verify), then the legacy root field, then the project name.
 */
export const useActiveCompany = (): ActiveCompanyIdentity => {
    const { application, organizationData } = useAuth()

    return useMemo(() => {
        const applications = organizationData?.applications ?? []
        const liveCompany = application?.settings?.company
        const summary = applications.find((app) => app.id === application?.id)
        const legacyName = (application as (ClientApplication & LegacyCompanyField) | null)?.company?.name

        const realName = liveCompany?.name || summary?.company?.name || legacyName
        const projectName = application?.projectName

        return {
            companyName: realName || projectName || 'Company',
            hasRealCompanyName: !!realName,
            mcNumber: liveCompany?.mcNumber || summary?.company?.mcNumber,
            dotNumber: liveCompany?.dotNumber || summary?.company?.dotNumber,
            projectName,
            applicationId: application?.id,
            isMultiCompany: !!organizationData?.multiMcEnabled && applications.length > 1,
            companyCount: applications.length,
        }
    }, [application, organizationData])
}
